import Text from '@ui/text/text/text';
import SettingsToggle from '../../controls/settings-toggle';
import { startTransition, useCallback, useMemo, useOptimistic, useState } from 'react';
import PreshowVideoWidget from './preshow-video-widget';
import {
  getIsSettingsUpdating,
  getWorkingProjectUuid,
  selectIsSingleSession,
  selectProjectSettings,
  selectWorkingProject,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import { SessionTypesEnum } from '@/types/session.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  saveFieldOnAllSessions,
  saveWorkingProject,
  saveWorkingProjectSessionByField,
  updateProjectSettings,
} from '@/store/slices/project';
import { PageModule, PageModuleType } from '@/types/module.types';
import { aggregateSpeakersOnSync } from '@/utils/speakers/aggregate-speakers-on-sync';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { selectFeedbackEnabled, selectShareEnabled, selectShowCalendar } from '@/store/slices/settings/selectors';
import { EventSettings } from '@/types/projects.types';
import { ICalendarDetailMetadata } from '../../settings.types';
import CalendarDetail from './calendar-detail';
import useLanguageParams from '@/utils/use-language-params';
import deepEqual from 'fast-deep-equal';
import UserFeedbackSurveyText from './user-feedback-survey-text';
import OptionalComponent from '@ui/optional-component/optional-component';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';

const ContentWidget = () => {
  const workingSession = useAppSelector(selectWorkingSession);
  const workingProject = useAppSelector(selectWorkingProject);
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);
  const reduxShowAddToCalendar = useAppSelector(selectShowCalendar);
  const settings = useAppSelector(selectProjectSettings);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const language = useLanguageParams() ?? 'en';
  const reduxShareEnabled = useAppSelector(selectShareEnabled);
  const reduxFeedbackEnabled = useAppSelector(selectFeedbackEnabled);
  const isSingleSession = useAppSelector(selectIsSingleSession);

  const session = useMemo(() => {
    if (workingSession) return workingSession;
    return workingProject?.sessions[0];
  }, [workingSession, workingProject]);

  const [isOnDemand, setIsOnDemand] = useOptimistic(session?.session_type === SessionTypesEnum.onDemand);
  const [replayOn, setReplayOn] = useOptimistic(!!session?.replay_on);
  const [showAddToCalendar, setShowAddToCalendar] = useOptimistic(reduxShowAddToCalendar);
  const [shareEnabled, setShareEnabled] = useOptimistic(reduxShareEnabled);
  const [feedbackEnabled, setFeedbackEnabled] = useOptimistic(reduxFeedbackEnabled);
  const [syncSpeakers, setSyncSpeakers] = useOptimistic(
    !!workingProject?.sessions?.[0]?.modules?.find((m) => m.type === PageModuleType.speakers)?.content?.sync_speakers
  );
  const [doNotRecord, setDoNotRecord] = useOptimistic(session?.streaming_options?.do_not_record);

  const [metadata, setMetadata] = useState<{ [key: string]: ICalendarDetailMetadata }>({});
  const [savingFeedback, setSavingFeedback] = useState(false);

  const canDisableRecordings = useCanUseChannelFeature(ChannelFeaturesEnum.allow_stream_without_recording);

  const dispatch = useAppDispatch();

  const handleToggle = useCallback(
    (sKey: string, on: boolean) => {
      if (!session) return;
      switch (sKey) {
        case 'on_demand': {
          startTransition(() => {
            setIsOnDemand(on);
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                session_type: on ? SessionTypesEnum.onDemand : SessionTypesEnum.broadcast,
              })
            );
          });
          break;
        }
        case 'replay_on': {
          startTransition(() => {
            setReplayOn(on);
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                replay_on: on,
              })
            );
          });
          break;
        }
        case 'do_not_record': {
          startTransition(() => {
            setDoNotRecord(on);
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                streaming_options: {
                  ...session.streaming_options,
                  do_not_record: !on, // Opposite of the toggle becuase the setting is negated and recording defaults to true without this property
                },
              })
            );
          });
          break;
        }
        case 'share_enabled': {
          if (!projectUuid || !settings || !workingProject?.sessions) return;
          startTransition(async () => {
            setShareEnabled(on);
            const promises: unknown[] = [];
            if (workingSession) {
              promises.push(
                dispatch(saveWorkingProjectSessionByField({ enable_share: on, uuid: workingSession.uuid }))
              );
            }
            promises.push(dispatch(saveFieldOnAllSessions({ enable_share: on })));
            await Promise.all(promises);
          });
          break;
        }
        case 'feedback': {
          if (!projectUuid || !settings || !workingProject?.sessions) return;
          startTransition(async () => {
            setSavingFeedback(true);
            setFeedbackEnabled(on);
            const promises: unknown[] = [];
            if (workingSession) {
              promises.push(
                dispatch(saveWorkingProjectSessionByField({ enable_feedback_survey: on, uuid: workingSession.uuid }))
              );
            }
            promises.push(dispatch(saveFieldOnAllSessions({ enable_feedback_survey: on })));
            await Promise.all(promises);
            setSavingFeedback(false);
          });
          break;
        }
        case 'sync_speakers': {
          if (!workingProject) return;
          startTransition(() => {
            setSyncSpeakers(on);
            const withUpdatedSpeakerModuleContent = (modules: PageModule[]) =>
              modules.map((m) =>
                m.type === PageModuleType.speakers ? { ...m, content: { ...m.content, sync_speakers: on } } : m
              );

            let updatedSessions = workingProject.sessions.map((s) => ({
              ...s,
              modules: withUpdatedSpeakerModuleContent(s.modules),
            }));

            if (on && isSingleSession) {
              updatedSessions = aggregateSpeakersOnSync(updatedSessions, workingProject.homepage);
            }

            const updatedProject = {
              ...workingProject,
              homepage: workingProject.homepage
                ? {
                    ...workingProject.homepage,
                    modules: withUpdatedSpeakerModuleContent(workingProject.homepage.modules),
                    post_register_home_modules: withUpdatedSpeakerModuleContent(
                      workingProject.homepage.post_register_home_modules
                    ),
                  }
                : workingProject.homepage,
              sessions: updatedSessions,
            };
            dispatch(saveWorkingProject({ project: updatedProject, sessions: updatedSessions }));
          });
          break;
        }
        default: {
          console.warn(`Unknown setting key ${sKey}`);
        }
      }
    },
    [
      dispatch,
      isSingleSession,
      session,
      setIsOnDemand,
      setReplayOn,
      setShareEnabled,
      setFeedbackEnabled,
      setSyncSpeakers,
      setDoNotRecord,
      projectUuid,
      settings,
      workingProject,
      workingSession,
    ]
  );

  const handleCalendarToggle = async (_val: string, on: boolean) => {
    if (!settings || isSettingsUpdating || !projectUuid) return;
    const projectSettings: EventSettings = { ...settings, show_add_to_cal_btn: on };
    startTransition(async () => {
      setShowAddToCalendar(on);
      await dispatch(updateProjectSettings({ event_uuid: projectUuid, settings: projectSettings }));
    });
  };

  const handleMetadataChange = (sessionUuid: string, updatedData: ICalendarDetailMetadata) => {
    setMetadata((prev) => ({
      ...prev,
      [sessionUuid]: { ...(prev[sessionUuid] || {}), ...updatedData },
    }));
  };

  const handleCalendarSave = () => {
    if (!session || Object.keys(metadata).length === 0) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: session.uuid,
        calendar_settings: {
          ...session.calendar_settings,
          content: {
            ...session.calendar_settings?.content,
            [language]: {
              ...session.calendar_settings?.content?.[language],
              ...(metadata[session.uuid]?.name && { title: metadata[session.uuid].name }),
              ...(metadata[session.uuid]?.description && { description: metadata[session.uuid].description }),
            },
          },
        },
      })
    );
    setMetadata({});
  };

  const handleCalendarCancel = () => setMetadata({});

  return (
    <>
      <div className="flex-column gap-8">
        <Text className="H5" color="var(--gray-100)">
          Content
        </Text>
        <PreshowVideoWidget />
        {session?.timestamp && (
          <SettingsToggle
            label="Add to Calendar"
            value={showAddToCalendar}
            sKey=""
            gap="0px"
            onToggle={handleCalendarToggle}
          >
            <SlideDownCard autoGrow animateOnInitialLoad={false} open={session && showAddToCalendar}>
              <CalendarDetail
                session={session}
                metadata={metadata[session.uuid] || {}}
                setMetadata={(data) => handleMetadataChange(session.uuid, data)}
                onCancel={handleCalendarCancel}
                onSave={handleCalendarSave}
                saveDisabled={deepEqual(metadata, {})}
              />
            </SlideDownCard>
          </SettingsToggle>
        )}
        <SettingsToggle label="Share" value={!!shareEnabled} sKey="share_enabled" onToggle={handleToggle} />
        <SettingsToggle
          label="Feedback Survey"
          value={!!feedbackEnabled}
          sKey="feedback"
          onToggle={handleToggle}
          gap="0px"
          tooltipText="When your session ends, a 5 star rating modal will populate for all users"
        >
          <SlideDownCard autoGrow animateOnInitialLoad={false} open={!!feedbackEnabled}>
            <UserFeedbackSurveyText saving={savingFeedback} />
          </SlideDownCard>
        </SettingsToggle>
        <SettingsToggle
          label="Auto-Replay"
          value={replayOn}
          sKey={'replay_on'}
          loading={!session}
          onToggle={handleToggle}
          tooltipText="After your livestream ends, immediately populates the video for on demand viewing"
        />
        <OptionalComponent display={canDisableRecordings}>
          <SettingsToggle
            label="Enable Recording"
            sKey="do_not_record"
            value={!doNotRecord}
            loading={!session}
            onToggle={handleToggle}
            tooltipText="Enables a live stream to be recorded"
          />
        </OptionalComponent>
        <SettingsToggle
          disabled={
            ![SessionTypesEnum.broadcast, SessionTypesEnum.onDemand].includes(session?.session_type as SessionTypesEnum)
          }
          label="On Demand"
          value={isOnDemand}
          sKey={'on_demand'}
          loading={!session}
          onToggle={handleToggle}
          tooltipText="Changes date & time module on the Overview page to allow for video upload"
        />
        {isSingleSession && (
          <SettingsToggle
            label="Sync Speakers"
            value={syncSpeakers}
            sKey="sync_speakers"
            onToggle={handleToggle}
            tooltipText="Enabling Sync Speakers will display the speakers selected on the Overview tab on all pages where you have the speaker module enabled."
          />
        )}
      </div>
    </>
  );
};

export default ContentWidget;
